import React, { useEffect, useState } from "react";
import { CalendarDays, Mail, Save, Play } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/api";
import { useLang } from "@/i18n";
import { Switch } from "@/components/ui/switch";
import { WEEKDAYS, todayKey } from "@/data/workouts";

const authHeader = () => ({ headers: { "X-Access-Token": localStorage.getItem("guardia_bp_token") || "" } });

export default function WeeklyCalendar({ programs, onStart }) {
  const { lang } = useLang();
  const [days, setDays] = useState(null);
  const [reminders, setReminders] = useState(true);
  const [saving, setSaving] = useState(false);
  const today = todayKey();
  const byId = Object.fromEntries(programs.map((p) => [p.id, p]));

  useEffect(() => {
    api.get("/blueprint/schedule", authHeader()).then((r) => { setDays(r.data.days); setReminders(r.data.reminders); }).catch(() => setDays({}));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/blueprint/schedule", { days, reminders, lang }, authHeader());
      toast.success(lang === "it" ? "Calendario salvato" : "Schedule saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  if (!days) return <p className="text-zinc-500 text-sm">Loading…</p>;

  return (
    <div data-testid="weekly-calendar">
      <div className="flex items-center gap-2 mb-1">
        <CalendarDays size={18} className="text-[#E63946]" />
        <h2 className="font-heading text-2xl font-extrabold uppercase text-white">{lang === "it" ? "La tua settimana" : "Your week"}</h2>
      </div>
      <p className="text-sm text-zinc-400 mb-6">{lang === "it" ? "Assegna un programma a ogni giorno. Lascia vuoto per il riposo." : "Assign a program to each day. Leave empty for rest."}</p>

      <div className="space-y-2">
        {WEEKDAYS.map((d) => {
          const pid = days[d.key] || "";
          const isToday = d.key === today;
          return (
            <div key={d.key} data-testid={`calendar-day-${d.key}`}
              className={`flex items-center gap-3 rounded-lg border px-4 py-3 ${isToday ? "border-[#E63946] bg-[#E63946]/5" : "border-[#272b35] bg-[#14161b]"}`}>
              <div className="w-24 sm:w-32 shrink-0">
                <span className="font-heading text-lg font-bold uppercase text-white">{d[lang]}</span>
                {isToday && <span className="block text-[10px] font-mono uppercase tracking-widest text-[#E63946]">{lang === "it" ? "Oggi" : "Today"}</span>}
              </div>
              <select value={pid} data-testid={`calendar-select-${d.key}`} onChange={(e) => setDays((s) => ({ ...s, [d.key]: e.target.value }))}
                className="flex-1 min-w-0 bg-[#0c0d10] border border-[#272b35] rounded-md px-3 py-2 text-sm text-white">
                <option value="">{lang === "it" ? "— Riposo —" : "— Rest day —"}</option>
                {programs.map((p) => <option key={p.id} value={p.id}>{p.title[lang]}{p.custom ? " ★" : ""}</option>)}
              </select>
              {pid && byId[pid] && (
                <button onClick={() => onStart(byId[pid])} data-testid={`calendar-start-${d.key}`} className="p-2 text-[#E63946] hover:text-white" title="Start">
                  <Play size={16} />
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-6 flex items-center justify-between gap-4 rounded-lg border border-[#272b35] bg-[#14161b] px-4 py-4">
        <div className="flex items-start gap-3">
          <Mail size={18} className="text-zinc-400 mt-0.5" />
          <div>
            <p className="text-sm text-white font-semibold">{lang === "it" ? "Promemoria email" : "Email reminders"}</p>
            <p className="text-xs text-zinc-500">{lang === "it" ? "Alle 8:00 (ora italiana) nei giorni con un programma" : "At 8:00 (Italian time) on days with a program"}</p>
          </div>
        </div>
        <Switch checked={reminders} onCheckedChange={setReminders} data-testid="calendar-reminders-switch" />
      </div>

      <button onClick={save} disabled={saving} data-testid="calendar-save-btn"
        className="mt-6 inline-flex items-center gap-2 px-8 py-3 bg-[#E63946] hover:bg-[#D90429] text-white font-bold uppercase rounded-md disabled:opacity-50">
        <Save size={16} /> {lang === "it" ? "Salva settimana" : "Save week"}
      </button>
    </div>
  );
}
